import type { TFunction } from 'i18next';

import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import { downloadReportPdf } from '@/utils/report-pdf';
import { formatDateTime } from '@/utils/format';

import { getCustomReportSource } from './custom-report-sources';
import { fetchCustomReportForExport } from './fetch-custom-report-for-export';
import { formatCustomReportCell } from './format-custom-report-cell';
import { customReportExportFilename } from './custom-report-export-filename';

export const downloadCustomReportPdfExport = async (input: {
  reportId: string;
  reportTitle: string;
  reportDescription?: string | null;
  objectSource: string;
  churchName: string;
  branchName?: string | null;
  logoUrl?: string | null;
  locale: string;
  timezone: string;
  t: TFunction<'reports'>;
  tCommon: TFunction;
}): Promise<void> => {
  const data = await fetchCustomReportForExport({ reportId: input.reportId });
  const source = getCustomReportSource(input.objectSource);
  const columnLabels = data.columns.map((key) => input.t(`custom.fields.${key}`, key));
  const rows = data.rows.map((row) =>
    data.columns.map((key) => {
      const field = source?.fields.find((item) => item.key === key);
      return formatCustomReportCell(field, row[key], row, input.t, input.locale);
    }),
  );

  await downloadReportPdf({
    churchName: input.churchName,
    footerLabel: input.tCommon('app.fullName'),
    branchName: input.branchName,
    logoUrl: input.logoUrl,
    title: input.reportTitle,
    description: input.reportDescription ?? '',
    generatedLabel: input.t('run.pdfGenerated', {
      when: formatDateTime(new Date().toISOString(), {
        locale: input.locale,
        timezone: input.timezone,
        withZone: true,
      }),
    }),
    columns: data.columns.map((key) => ({ key, type: 'string' as const })),
    columnLabels,
    rows,
    filename: customReportExportFilename(input.reportTitle, 'pdf'),
  });

  try {
    await api.post<void>(`/reports/custom/${input.reportId}/export`, { format: 'pdf' });
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(500);
  }
};
